import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Lightbulb, AlertCircle } from 'lucide-react';
import { useAuth } from '@/lib/hooks/useAuth';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { signIn } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error } = await signIn(email, password);
      
      if (error) throw error;
      
      // Successful login will trigger auth state change and redirect
      navigate('/dashboard');
    } catch (err) {
      console.error('Sign in error:', err);
      if (err instanceof Error) {
        if (err.message.includes('Invalid login credentials')) {
          setError('Invalid email or password');
        } else if (err.message === 'Authentication failed') {
          setError('Login failed. Please try again.');
        } else {
          setError(err.message);
        }
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#212323] flex flex-col items-center justify-center px-4">
      <div className="flex items-center mb-8">
        <Lightbulb className="h-16 w-16 text-[#F8B500]" />
        <h1 className="text-4xl font-bold text-white ml-4">
          PITCH<span className="text-[#F8B500]">lift</span>
        </h1>
      </div>

      <h2 className="text-3xl font-bold text-[#F8B500] mb-16">
        sign in
      </h2>

      <div className="w-full max-w-md">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-500/10 text-red-500 p-4 rounded-md flex items-start">
              <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="mt-1 bg-gray-300 text-gray-900 placeholder-gray-600"
              required
            />
          </div>

          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className="mt-1 bg-gray-300 text-gray-900 placeholder-gray-600"
              required
            />
          </div>

          <Button
            type="submit"
            className="w-full py-6 text-lg"
            size="lg"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </Button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-gray-400">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#F8B500] hover:text-[#F8B500]/80">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}